import {
  Archive,
  ArrowRight,
  CalendarDays,
  CheckCircle2,
  Edit3,
  RotateCcw,
  Trash2,
} from "lucide-react";
import { Link } from "react-router-dom";
import { deadlineLabel, formatCurrency, progressPercentage } from "@/lib/goals";

const objectiveLabel = (goal) => {
  if (goal.purpose_category === "Prefiro não informar") return "Não informado";
  return goal.purpose_detail || goal.purpose_category;
};

const statusLabel = (goal) => {
  if (goal.status === "scheduled" && goal.scheduled_for) {
    return `Começa em ${new Date(`${goal.scheduled_for}T12:00:00`).toLocaleDateString("pt-BR")}`;
  }
  if (goal.is_complete) return "Concluída";
  return "Em andamento";
};

export const GoalManagerCard = ({ goal, archived, onEdit, onArchive, onDelete }) => {
  const progress = progressPercentage(goal);

  return (
    <li
      data-testid={`goal-card-${goal.id}`}
      className="rounded-2xl border border-amber-200 bg-white p-5 shadow-sm"
    >
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <p className="text-sm font-bold uppercase tracking-wide text-amber-800">
            {statusLabel(goal)}
          </p>
          <h2
            data-testid={`goal-title-${goal.id}`}
            className="mt-1 break-words font-heading text-2xl font-extrabold text-emerald-950"
          >
            {goal.title}
          </h2>
          <p data-testid={`goal-objective-${goal.id}`} className="mt-1 text-base text-slate-600">
            {objectiveLabel(goal)}
          </p>
        </div>
        {goal.is_complete && <CheckCircle2 className="h-7 w-7 shrink-0 text-emerald-600" />}
      </div>
      <dl className="mt-5 grid grid-cols-2 gap-x-4 gap-y-4">
        <div>
          <dt className="text-sm font-semibold text-slate-600">Valor total</dt>
          <dd data-testid={`goal-target-amount-${goal.id}`} className="mt-1 text-lg font-extrabold text-emerald-950">
            {formatCurrency(goal.amount_cents)}
          </dd>
        </div>
        <div>
          <dt className="text-sm font-semibold text-slate-600">Guardado</dt>
          <dd data-testid={`goal-saved-amount-${goal.id}`} className="mt-1 text-lg font-extrabold text-emerald-950">
            {formatCurrency(goal.saved_cents)}
          </dd>
        </div>
        <div>
          <dt className="text-sm font-semibold text-slate-600">Quanto falta</dt>
          <dd data-testid={`goal-remaining-amount-${goal.id}`} className="mt-1 text-lg font-extrabold text-emerald-950">
            {formatCurrency(goal.remaining_cents)}
          </dd>
        </div>
        <div>
          <dt className="flex items-center gap-1 text-sm font-semibold text-slate-600">
            <CalendarDays className="h-4 w-4" /> Prazo
          </dt>
          <dd className="mt-1 text-base font-bold text-emerald-950">{deadlineLabel(goal)}</dd>
        </div>
      </dl>
      <div className="mt-5">
        <div className="flex items-center justify-between gap-3">
          <span className="text-base font-bold text-emerald-950">Progresso</span>
          <span data-testid={`goal-percentage-${goal.id}`} className="text-base font-extrabold text-emerald-800">
            {progress}% concluído
          </span>
        </div>
        <div className="mt-2 h-3 overflow-hidden rounded-full bg-amber-100">
          <div
            data-testid={`goal-progress-bar-${goal.id}`}
            className="h-full rounded-full bg-emerald-600 transition-[width] duration-300"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>
      <div className="mt-6 grid grid-cols-2 gap-2">
        <Link
          to={`/metas/${goal.id}`}
          data-testid={`open-goal-btn-${goal.id}`}
          className="inline-flex min-h-[46px] items-center justify-center gap-2 rounded-xl bg-emerald-700 px-3 text-base font-bold text-white hover:bg-emerald-800"
        >
          Abrir meta <ArrowRight className="h-4 w-4" />
        </Link>
        <button
          type="button"
          onClick={() => onEdit(goal)}
          data-testid={`edit-goal-name-btn-${goal.id}`}
          className="inline-flex min-h-[46px] items-center justify-center gap-2 rounded-xl border-2 border-emerald-200 bg-white px-3 text-base font-bold text-emerald-900 hover:bg-emerald-50"
        >
          <Edit3 className="h-4 w-4" /> Editar nome
        </button>
        <button
          type="button"
          onClick={() => onArchive(goal)}
          data-testid={`archive-goal-btn-${goal.id}`}
          className="inline-flex min-h-[46px] items-center justify-center gap-2 rounded-xl border-2 border-amber-200 bg-amber-50 px-3 text-base font-bold text-amber-950 hover:bg-amber-100"
        >
          {archived ? <RotateCcw className="h-4 w-4" /> : <Archive className="h-4 w-4" />}
          {archived ? "Reativar" : "Arquivar"}
        </button>
        <button
          type="button"
          onClick={() => onDelete(goal)}
          data-testid={`delete-goal-btn-${goal.id}`}
          className="inline-flex min-h-[46px] items-center justify-center gap-2 rounded-xl border-2 border-red-200 bg-white px-3 text-base font-bold text-red-800 hover:bg-red-50"
        >
          <Trash2 className="h-4 w-4" /> Excluir
        </button>
      </div>
    </li>
  );
};